import type { ReactNode } from "react";
import { ErrorState } from "@/ds";
import type { ObraListaItem, ObrasSortField } from "@/modules/obras";
import { ObrasListaTable, TipoObraTags } from "@/shared/components";
import {
  formatDuration,
  formatInteger,
  formatMillions,
  formatObraDate,
  formatPercent,
} from "../../obras-lista.utils";
import { OBRAS_NO_VALUE } from "../../obras-lista.constants";
import type { ObrasColumnKey } from "../../types-obras-lista";
import { ObrasEmpty } from "../obras-empty";
import type { ObrasTableViewModel } from "./types-obras-table";

/** Conteúdo da célula; indicadores sem fonte de dados chegam `null` e viram "—" (Impasse 14). */
function renderCell(key: ObrasColumnKey, obra: ObraListaItem, compacto: boolean): ReactNode {
  switch (key) {
    case "id":
      return obra.codigo;
    case "embarcacao":
      return obra.embarcacao.nome || OBRAS_NO_VALUE;
    case "statusObra":
      return obra.statusObra?.descricao || OBRAS_NO_VALUE;
    case "tipoObra":
      return <TipoObraTags tipos={obra.tiposObra} compacto={compacto} />;
    case "condicao":
      return obra.condicao ?? OBRAS_NO_VALUE;
    case "inicio":
      return formatObraDate(obra.inicio);
    case "termino":
      return formatObraDate(obra.fim);
    case "duracao":
      return formatDuration(obra.duracaoDias);
    case "realizado":
      return formatMillions(obra.realizadoBrlMilhoes);
    case "totalServicos":
      return formatInteger(obra.totalServicos);
    case "percServicos":
      return formatPercent(obra.percentualServicosConcluidos);
    case "totalMateriais":
      return formatInteger(obra.totalMateriais);
    case "percMateriais":
      return formatPercent(obra.percentualMateriaisEntregues);
    case "totalFacilidades":
      return formatInteger(obra.totalFacilidades);
    case "percFacilidades":
      return formatPercent(obra.percentualFacilidadesConsumidas);
    default:
      return null;
  }
}

/**
 * Visão Lista (HU 2521): `HubTable` paginado, ordenado e filtrado no servidor. ID e
 * Embarcação ficam fixas à esquerda e a lupa de Detalhamento à direita; no mobile, ID e
 * embarcação formam a coluna Obra, fixa.
 */
export function ObrasTableView({
  search,
  filters,
  onRetry,
  onClearSearch,
  onClearFilters,
  ...tabela
}: ObrasTableViewModel) {
  return (
    <ObrasListaTable<ObraListaItem, ObrasColumnKey, ObrasSortField>
      {...tabela}
      renderCell={renderCell}
      empty={
        <ObrasEmpty
          search={search}
          filters={filters}
          onClearSearch={onClearSearch}
          onClearFilters={onClearFilters}
        />
      }
      error={
        <ErrorState
          message="Não foi possível carregar as obras."
          description={tabela.errorMessage ?? undefined}
          onRetry={onRetry}
          testId="obras-error"
        />
      }
    />
  );
}
